<template>
	<common-card className="omapi-template-preview" :size="4" :class="{'previewing': isOpening || isPreviewing}">
		<img class="omapi-template-preview__image" :src="imgUrl" alt="Template Preview">
		<div class="omapi-template-preview__actions omapi-card__flex">
			<core-button
				color="green"
				class="omapi-template-preview__actions__use"
				@click.prevent="createCampaign"
			>Use Template</core-button>
			<core-loading-button
				class="omapi-template-preview__actions__open"
				color="transparent"
				:isLink="true"
				:href="previewUrl"
				@click.prevent.stop="preview"
				:loading="isOpening"
			>
				Preview
			</core-loading-button>
		</div>

		<div
			v-if="'inline' === template.type"
			class="campaign-inline-preview"
			:class="{'is-loading': isOpening}"
			:id="`campaign-${slug}`"
			v-show="isPreviewing"
			@click="maybeClose"
		>
			<div class="campaign-inline-preview__inner" :id="`om-${slug}-holder`">
				<svg-loading></svg-loading>
			</div>
		</div>
		<template v-slot:footer>
			<div class="omapi-template-preview__title omapi-card__flex">
				<span v-if="isMobile" class="omapi-template-preview__mobile-badge" title="Mobile Campaign">
					<svg-mobile></svg-mobile>
				</span>
				{{ template.name }}
			</div>
			<span v-if="isNew" class="omapi-template-preview__new-badge om-new-badge">
				New
			</span>
		</template>
	</common-card>
</template>

<script>
import { closeCampaigns } from '@/utils/om-api'
import { createNamespacedHelpers } from 'vuex'
const { mapState } = createNamespacedHelpers('templates')

export default {
	props : [ 'template' ],
	data () {
		return {
			tryCount : 0,
			isNew    : false,
			isMobile : false,
		}
	},
	created () {
		document.addEventListener('om.Campaign.afterShow', this.afterShow)
		document.addEventListener('om.Campaign.afterClose', this.afterClose)
	},
	beforeDestroy () {
		document.removeEventListener('om.Campaign.afterShow', this.afterShow)
		document.removeEventListener('om.Campaign.afterClose', this.afterClose)
	},
	mounted () {
		this.isMobile = this.template.mobile

		const created   = this.$moment(this.template.created_at, 'YYYY-MM-DD HH:mm:ss')
		const compare   = this.$moment().subtract(3, 'month')
		this.isNew = created.isAfter(compare)
	},
	computed : {
		...mapState([ 'types', 'loadingPreview', 'previewing', 'apiLoaded' ]),
		slug () {
			return this.template.demo_campaign_slug
		},
		typeObject () {
			return this.$get(`types[${this.template.type}]`)
		},
		isOpening () {
			return this.slug === this.loadingPreview
		},
		isPreviewing () {
			return this.slug === this.previewing
		},
		previewUrl () {
			return this.$urls.app(`c/${this.slug}`)
		},
		upsellUrl () {
			return this.$urls.app('account/upgrade?utm_source=app&utm_medium=upsell&utm_campaign=templates&feature=templates')
		},
		features () {
			const feature = this.$get('typeObject.feature')
			return feature ? [ feature ] : []
		},
		imgUrl () {
			const parts = this.template.image_url.split('/campaign-templates')
			if (parts[1]) {
				return this.$urls.cdn('campaign-templates' + parts[1])
			}

			const url = new URL(this.template.image_url)

			return this.template.image_url
				.replace(url.protocol + '//' + url.hostname, this.$urls.cdn())
		},
		userHasAccessToTemplate () {
			return (!(
				this.template.premium.length && this.template.premium.some(feature => !this.$store.getters.hasFeature(feature))
			))
		},
	},
	methods : {
		createCampaign () {
			this.$store.commit('templates/setActiveTemplate', this.template)

			if (!this.$store.getters.connected) {
				return this.$modal.show('not-connected')
			}

			return this.userHasAccessToTemplate
				? this.$modal.show('create-campaign-modal')
				: this.$modal.show('no-access')
		},
		afterShow (e) {
			const { Campaign: c } = e.detail
			if (c && c.id === this.slug) {
				this.doneLoadingPreview()
			}
		},
		afterClose (e) {
			const { Campaign: c } = e.detail
			if (c && c.id === this.slug) {
				this.donePreviewing()
			}
		},
		preview () {
			if ((this.isOpening || this.isPreviewing) && !this.tryCount) {
				return false
			}
			this.$store.commit('templates/setLoadingPreview', this.slug)
			this.$store.commit('templates/setPreviewing', this.slug)

			const campaign = window[`om${this.slug}`]

			if (campaign) {
				return closeCampaigns(c => this.slug !== c.id && !c.options.mobile)
					// If we had to close some campaigns, we need to wait for the close
					// animations to complete before opening the new campaign.
					? campaign.Timeouts.set(() => campaign.startShow(), 200)
					: campaign.startShow()
			}

			this.fallbackOpen()
		},
		fallbackOpen () {
			if (!this.apiLoaded && 20 > this.tryCount) {
				this.tryCount++
				return setTimeout(() => this.preview(), 500)
			}

			// After 20 seconds, we give up, and simply open the preview monster-campaign
			window.open(this.previewUrl)
			setTimeout(() => {
				this.doneLoadingPreview()
				this.donePreviewing()
			}, 1000)
		},
		doneLoadingPreview () {
			if (this.isOpening) {
				this.$store.commit('templates/setLoadingPreview', '')
			}
		},
		donePreviewing () {
			if (this.isPreviewing) {
				this.$store.commit('templates/setPreviewing', '')
			}
		},
		maybeClose (e) {
			if (`campaign-${this.slug}` === e.target.id) {
				const campaign = window[`om${this.slug}`]
				if (campaign) {
					campaign.startClose(true)
				}
				this.donePreviewing()
			}
		},
	},
}
</script>
